import type { Graph3DAvatarNodeData, Graph3DData } from '@packages/graph-3d';
import {
  autoWrapTextBox,
  computeFixedLinesBoxHeight,
  computeLayerOffsets,
} from '@packages/graph-3d';
import type {
  ProcessGraphCompileResult,
  ProcessNodeData,
  ResultNodeData,
  RoleNodeData,
} from '../utils/processGraph';

export type ProcessGraph3DNodeData =
  | {
      kind: 'process';
      originalName: string;
      processName: string;
      themeColor: string;
      themeSoftBg: string;
      themeId: string;
      numbering: string;
      isRoot: boolean;
      isLeaf: boolean;
    }
  | ({
      kind: 'role';
      processName: string;
      parentProcessId: string;
      roleName: string;
      themeColor: string;
      themeSoftBg: string;
      themeId: string;
    } & Graph3DAvatarNodeData)
  | {
      kind: 'result';
      originalName: string;
      processName: string;
      resultFor: string;
      themeColor: string;
      themeSoftBg: string;
      themeId: string;
      numbering: string;
      isRoot: false;
      isLeaf: true;
    };

export interface ProcessGraph3DEdgeData {
  themeColor: string;
  edgeKind: 'process' | 'result' | 'role';
  labelOffsetX?: number;
  labelOffsetY?: number;
  routeStyle?: 'straight' | 'orthogonal';
  showArrow?: boolean;
}

const PROCESS_MIN_EDGE_LENGTH = 96;
const PROCESS_ROOT_TEXT = {
  fontSize: 19,
  fontWeight: 700,
  lineHeight: 24,
  paddingX: 72,
  paddingY: 44,
  minWidth: 200,
  minHeight: 96,
  maxWidth: 340,
} as const;
const PROCESS_BRANCH_TEXT = {
  fontSize: 15,
  fontWeight: 700,
  lineHeight: 19,
  paddingX: 64,
  paddingY: 36,
  minWidth: 160,
  minHeight: 80,
  maxWidth: 260,
} as const;
const PROCESS_LEAF_TEXT = {
  fontSize: 15,
  fontWeight: 700,
  lineHeight: 19,
  paddingX: 64,
  paddingY: 36,
  minWidth: 160,
  minHeight: 76,
  maxWidth: 240,
} as const;
const RESULT_TEXT = {
  fontSize: 11.5,
  fontWeight: 700,
  lineHeight: 15,
  paddingX: 18,
  paddingY: 26,
  width: 256,
  maxLines: 3,
} as const;
const RESULT_NODE_WIDTH = RESULT_TEXT.width;
const RESULT_NODE_HEIGHT = computeFixedLinesBoxHeight(RESULT_TEXT);
const ROLE_NODE_WIDTH = 220;
const ROLE_NODE_HEIGHT = 84;
const PROCESS_SIDE_NODE_GAP_Y = 22;
const PROCESS_SIDE_RESULT_ALIGN_OFFSET_X =
  (RESULT_NODE_WIDTH - ROLE_NODE_WIDTH) / 2;

interface ProcessTreeMetric {
  id: string;
  y: number;
  maxDepth: number;
}

export function buildProcessGraph3DData(
  graph: ProcessGraphCompileResult,
  options: { includeResults: boolean }
): Graph3DData<ProcessGraph3DNodeData, ProcessGraph3DEdgeData> {
  const nodes: Graph3DData<
    ProcessGraph3DNodeData,
    ProcessGraph3DEdgeData
  >['nodes'] = [];
  const edges: Graph3DData<
    ProcessGraph3DNodeData,
    ProcessGraph3DEdgeData
  >['edges'] = [];
  const maxHalfWidthByDepth = new Map<number, number>();
  const trackHalfWidth = (depth: number, halfWidth: number) => {
    maxHalfWidthByDepth.set(
      depth,
      Math.max(maxHalfWidthByDepth.get(depth) ?? 0, halfWidth)
    );
  };
  const childrenByParent = new Map<string | null, ProcessNodeData[]>();
  graph.processIds.forEach(id => {
    const node = graph.processNodeMap.get(id);
    if (!node) {
      return;
    }
    const parentId = node.parentProcessId ?? null;
    const siblings = childrenByParent.get(parentId) ?? [];
    siblings.push(node);
    siblings.sort((left, right) => left.layoutSeq - right.layoutSeq);
    childrenByParent.set(parentId, siblings);
  });

  let leafCursorY = 0;
  let previousLeafHalfHeight = 0;
  let hasLeaf = false;
  const nextLeafRowY = (rowHalfHeight: number) => {
    const y = hasLeaf
      ? leafCursorY +
        previousLeafHalfHeight +
        PROCESS_MIN_EDGE_LENGTH +
        rowHalfHeight
      : rowHalfHeight;
    leafCursorY = y;
    previousLeafHalfHeight = rowHalfHeight;
    hasLeaf = true;
    return y;
  };

  const appendProcessNode = (node: ProcessNodeData, y: number) => {
    const rawLabel = node.label;
    const textConfig = node.isRoot
      ? PROCESS_ROOT_TEXT
      : node.isLeaf
        ? PROCESS_LEAF_TEXT
        : PROCESS_BRANCH_TEXT;
    const box = autoWrapTextBox(rawLabel, textConfig);
    trackHalfWidth(node.depth, box.width / 2);
    nodes.push({
      id: node.id,
      label: box.label,
      type: 'process',
      depth: node.depth,
      position: { x: 0, y: -y, z: node.depth * 18 },
      size: {
        width: box.width,
        height: box.height,
        depth: node.isRoot ? 24 : 20,
      },
      data: {
        kind: 'process',
        originalName: node.processName,
        processName: node.processName,
        themeColor: node.isRoot ? '#111827' : node.themeColor,
        themeSoftBg: node.isRoot ? '#e5e7eb' : node.themeSoftBg,
        themeId: node.themeId,
        numbering: node.numbering,
        isRoot: node.isRoot,
        isLeaf: node.isLeaf,
      },
    });
    if (node.parentProcessId) {
      edges.push({
        id: `e_${node.parentProcessId}_${node.id}`,
        source: node.parentProcessId,
        target: node.id,
        type: 'hierarchy',
        data: { themeColor: node.themeColor, edgeKind: 'process' },
      });
    }
    return { width: box.width, height: box.height };
  };

  const appendRoleNodes = (
    node: ProcessNodeData,
    y: number,
    includeResults: boolean
  ) => {
    const roleIds = graph.processToRoleIds.get(node.id) ?? [];
    const resultId = graph.processToResultId.get(node.id);
    const roleCount = roleIds.length;
    const sideStackHeight =
      roleCount > 0
        ? roleCount * ROLE_NODE_HEIGHT +
          roleCount * PROCESS_SIDE_NODE_GAP_Y +
          RESULT_NODE_HEIGHT
        : RESULT_NODE_HEIGHT;
    const sideTopY = y - sideStackHeight / 2;
    const roleDepth = node.depth + 1;

    if (roleCount > 0) {
      trackHalfWidth(roleDepth, ROLE_NODE_WIDTH / 2);
    }

    roleIds.forEach((roleId, index) => {
      const roleNode = graph.roleNodeMap.get(roleId);
      if (!roleNode) {
        return;
      }
      const roleY =
        sideTopY +
        ROLE_NODE_HEIGHT / 2 +
        index * (ROLE_NODE_HEIGHT + PROCESS_SIDE_NODE_GAP_Y);
      nodes.push({
        id: roleNode.id,
        label: roleNode.roleName,
        type: 'role',
        depth: roleDepth,
        position: { x: 0, y: -roleY, z: roleDepth * 18 },
        size: { width: ROLE_NODE_WIDTH, height: ROLE_NODE_HEIGHT, depth: 14 },
        data: {
          kind: 'role',
          processName: node.processName,
          parentProcessId: node.id,
          roleName: roleNode.roleName,
          themeColor: roleNode.themeColor,
          themeSoftBg: roleNode.themeSoftBg,
          themeId: roleNode.themeId,
          subtitle: roleNode.roleName,
          avatarFallbackUrls: [],
        },
      });
      edges.push({
        id: `e_${node.id}_${roleNode.id}`,
        source: node.id,
        target: roleNode.id,
        type: 'flow',
        label: '执行Agent',
        data: {
          themeColor: roleNode.themeColor,
          edgeKind: 'role',
          labelOffsetX: 14,
          labelOffsetY: 42,
          routeStyle: 'orthogonal',
          showArrow: false,
        },
      });
    });

    if (!includeResults || !resultId) {
      return;
    }

    const resultNode = graph.resultNodeMap.get(resultId);
    if (!resultNode) {
      return;
    }

    const resultY =
      sideTopY +
      roleCount * (ROLE_NODE_HEIGHT + PROCESS_SIDE_NODE_GAP_Y) +
      RESULT_NODE_HEIGHT / 2;
    const resultOffsetX =
      roleCount > 0 ? PROCESS_SIDE_RESULT_ALIGN_OFFSET_X : 0;
    trackHalfWidth(roleDepth, RESULT_NODE_WIDTH / 2 + resultOffsetX);
    nodes.push({
      id: resultNode.id,
      label: '—',
      type: 'result',
      depth: roleDepth,
      position: { x: resultOffsetX, y: -resultY, z: roleDepth * 18 },
      size: { width: RESULT_NODE_WIDTH, height: RESULT_NODE_HEIGHT, depth: 16 },
      data: {
        kind: 'result',
        originalName: node.processName,
        processName: node.processName,
        resultFor: node.id,
        themeColor: resultNode.themeColor,
        themeSoftBg: resultNode.themeSoftBg,
        themeId: resultNode.themeId,
        numbering: node.numbering,
        isRoot: false,
        isLeaf: true,
      },
    });
    edges.push({
      id: `e_${node.id}_${resultNode.id}`,
      source: node.id,
      target: resultNode.id,
      type: 'flow',
      label: '执行结果',
      data: {
        themeColor: resultNode.themeColor,
        edgeKind: 'result',
        labelOffsetX: 14,
        labelOffsetY: roleCount > 0 ? -42 : 10,
        routeStyle: roleCount > 0 ? 'orthogonal' : 'straight',
        showArrow: false,
      },
    });
  };

  const visit = (node: ProcessNodeData): ProcessTreeMetric => {
    const children = childrenByParent.get(node.id) ?? [];
    const childMetrics = children.map(visit);
    const processBox = appendProcessNode(node, 0);
    const roleCount = (graph.processToRoleIds.get(node.id) ?? []).length;
    const sideStackHeight =
      roleCount > 0
        ? roleCount * ROLE_NODE_HEIGHT +
          roleCount * PROCESS_SIDE_NODE_GAP_Y +
          RESULT_NODE_HEIGHT
        : RESULT_NODE_HEIGHT;
    const rowHalfHeight = Math.max(
      Math.max(processBox.height, RESULT_NODE_HEIGHT) / 2,
      sideStackHeight / 2
    );
    const y = node.isLeaf
      ? nextLeafRowY(rowHalfHeight)
      : average(childMetrics.map(item => item.y));
    nodes[nodes.length - 1].position = {
      ...nodes[nodes.length - 1].position,
      y: -y,
    };
    appendRoleNodes(node, y, options.includeResults && node.isLeaf);
    return {
      id: node.id,
      y,
      maxDepth: Math.max(
        node.depth,
        ...childMetrics.map(item => item.maxDepth)
      ),
    };
  };

  const roots = childrenByParent.get(null) ?? [];
  roots.forEach(visit);

  const layerOffsetByDepth = computeLayerOffsets(
    maxHalfWidthByDepth,
    PROCESS_MIN_EDGE_LENGTH
  );
  nodes.forEach(node => {
    const depth = node.depth ?? 0;
    const localOffsetX = node.position?.x || 0;
    node.position = {
      ...node.position,
      x: (layerOffsetByDepth.get(depth) ?? 0) + localOffsetX,
    };
  });

  const centerY = average(
    nodes
      .filter(node => node.type === 'process')
      .map(node => -(node.position?.y || 0))
  );

  nodes.forEach(node => {
    node.position = {
      ...node.position,
      y: (node.position?.y || 0) + centerY,
    };
  });

  return {
    nodes,
    edges,
  };
}

function average(values: number[]) {
  if (values.length === 0) {
    return 0;
  }
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}
